/**
 * PriceDelta — 손익·등락 표기 (공유).
 *
 * 왜 뽑았나: `value >= 0 ? "var(--price-up)" : "var(--price-down)"` 분기가
 * 홈·카드 상세·컬렉션 목록·컬렉션 상세에 아홉 번 복제돼 있었다. 색 규칙(서구식:
 * 상승 초록 ▲ / 하락 빨강 ▼)이 바뀌면 아홉 곳을 찾아다녀야 했다.
 *
 * ⚠ **표기는 지금 화면 그대로 유지한다** (2026-08-13 결정). 이 커밋은 중복만
 * 없애는 것이고, 금액에 ▲▼ 를 붙일지 같은 표기 규칙 통일은 별도 건이다.
 * 그래서 marker 를 형식별 기본값으로 두되 호출부가 덮을 수 있게 열어 뒀다:
 *   percent → ▲▼   (홈·카드 상세)
 *   amount  → +/-   (컬렉션)
 *
 * 색만으로 방향을 읽게 하지 않는다 — marker 가 항상 함께 나간다(색각 이상 대응).
 *
 * 2026-08-14: arrow 마커를 ▲▼ 문자에서 PEEKA GraphUp/GraphDown 아이콘으로 바꿨다.
 * ⚠ 아이콘을 sm(20px) 으로 두는 이유 — 이 그림은 선이 아이콘 폭의 4.2% 라
 *   12~16px 에서는 흐릿한 실선이 되어 방향이 안 읽힌다(실측). 색각 이상 대응이
 *   달린 자리라 옆 글자(12px)보다 커지더라도 읽히는 쪽을 골랐다.
 *
 * null(구매가 미입력 등) 은 화면마다 처리가 달라 여기서 다루지 않는다.
 * 어떤 곳은 '—', 어떤 곳은 아예 숨긴다 — 호출부가 판단한다.
 */
import { HStack } from "@astryxdesign/core/Layout";
import { useTranslation } from "react-i18next";
import { Text } from "@astryxdesign/core/Text";
import Icon from "../primitives/Icon";
import { formatKrw } from "../../lib/formatPrice";

/**
 * 손익 색 토큰. Heading 을 쓰거나 여러 값을 한 줄에 조합하는 자리처럼
 * PriceDelta 로 감싸기 어려운 곳에서 쓴다 — 색 규칙은 여기 한 곳에만 둔다.
 */
export function priceDeltaColor(value: number): string {
  return value >= 0 ? "var(--price-up)" : "var(--price-down)";
}

export type PriceDeltaFormat = "amount" | "percent";
export type PriceDeltaMarker = "sign" | "arrow";

export interface PriceDeltaProps {
  /** 손익/등락 값. 금액이면 원 단위, 퍼센트면 % 값 */
  value: number;
  format: PriceDeltaFormat;
  /** 미지정 시 amount→sign · percent→arrow (현행 표기) */
  marker?: PriceDeltaMarker;
  /** 앞에 붙일 기간 라벨 (예: "30일") */
  label?: string;
  /**
   * 글자 크기. 기본은 `supporting`(12px) — 등락·손익은 금액 옆에 붙는 **보조 수치**라
   * 금액과 같은 16px 이면 어느 쪽이 값인지 흐려진다 (2026-08-14, 홈 §1 기준).
   */
  type?: "label" | "supporting" | "body";
  weight?: "semibold" | "bold";
}

export default function PriceDelta({
  value,
  format,
  marker,
  label,
  type = "supporting",
  weight = "semibold",
}: PriceDeltaProps) {
  const { t } = useTranslation();
  const isUp = value >= 0;
  const mark = marker ?? (format === "percent" ? "arrow" : "sign");

  const color = priceDeltaColor(value);
  const amount =
    format === "percent"
      ? `${Math.abs(value).toFixed(1)}%`
      : formatKrw(Math.abs(value));

  if (mark === "arrow") {
    return (
      <HStack gap={1} vAlign="center">
        <Icon
          name={isUp ? "GraphUp" : "GraphDown"}
          size="sm"
          color={color}
          ariaLabel={isUp ? t("trend.up") : t("trend.down")}
        />
        <Text type={type} weight={weight} style={{ color }}>
          {label ? `${label} ` : ""}
          {amount}
        </Text>
      </HStack>
    );
  }

  // sign — formatKrw 가 음수에 '-' 를 붙이므로 양수일 때만 '+' 를 더한다
  const signed = `${isUp ? "+" : ""}${
    format === "percent" ? `${value.toFixed(1)}%` : formatKrw(value)
  }`;
  return (
    <Text type={type} weight={weight} style={{ color }}>
      {label ? `${label} ` : ""}
      {signed}
    </Text>
  );
}
